import { collapseThemeDisplayName } from 'features/charts-v2/utils/filter-dedup'
import {
  type FilterOption,
  type FilterOptionValue,
  FilterTypes,
} from 'features/filters/types'
import { splitThemeName } from 'features/project/normalize-theme-name'

const PARENT_CHILD_SEPARATOR = ': '

/**
 * Groups theme option values by the parent of their "Parent: Child" display
 * name. A value without a separator is a theme whose subtheme shares its name
 * (see `splitThemeName`), so it is its own parent's child. Run it on
 * `deduplicateFilterValues` output so same-named clusters count once.
 */
export function groupThemeValuesByParent(
  values: FilterOptionValue[],
): Record<string, FilterOptionValue[]> {
  const byParent: Record<string, FilterOptionValue[]> = {}
  for (const value of values) {
    ;(byParent[splitThemeName(value.value).theme] ??= []).push(value)
  }
  return byParent
}

/**
 * One synthetic value per parent that has two or more children, selecting the
 * union of its children's cluster uuids and summing their counts. The same
 * two-child threshold as `rollUpThemeDisplayNames`, so a parent row exists
 * exactly where a card would roll up to the parent's name; a single-child
 * parent would only duplicate its child. Run it on `deduplicateFilterValues`
 * output, like `groupThemeValuesByParent`.
 */
export function buildParentThemeValues(
  values: FilterOptionValue[],
): FilterOptionValue[] {
  return Object.entries(groupThemeValuesByParent(values)).flatMap(
    ([parent, children]) => {
      if (children.length < 2) return []
      const identifiers = [
        ...new Set(
          children.flatMap((child) => child.identifiers ?? [child.value]),
        ),
      ]
      return [
        {
          value: parent,
          n_posts: children.reduce(
            (sum, child) => sum + (child.n_posts ?? 0),
            0,
          ),
          identifiers,
          cluster_uuid: identifiers[0],
        },
      ]
    },
  )
}

// Charts 2 has no parent_theme filter field; expand the parent to the
// OR-of-sub-theme-cluster_uuids shape the native picker produces.
export function expandParentThemeNamesToSubThemeClusterUuids(
  parentNames: string[],
  filterOptions?: FilterOption[],
): string[] {
  const themeOption = filterOptions?.find(
    (opt) => opt.field === 'theme' && opt.filter_type === FilterTypes.SELECT,
  )
  if (!themeOption || themeOption.filter_type !== FilterTypes.SELECT) {
    return []
  }
  const uuids: string[] = []
  for (const parent of parentNames) {
    const prefix = `${parent}${PARENT_CHILD_SEPARATOR}`
    for (const v of themeOption.values) {
      if (
        v.cluster_uuid &&
        (v.value.startsWith(prefix) || v.value === parent)
      ) {
        uuids.push(v.cluster_uuid)
      }
    }
  }
  return uuids
}

/**
 * Display names for a theme selection, with every fully-selected parent
 * rolled up to its own name. A parent counts only when it has two or more
 * children in `themeOptionValues` (deduplicated by display name) and every
 * child's name is selected; a lone sub-theme is never relabelled as its whole
 * parent. Names under a rolled-up parent that are absent from the options
 * (label-only) are absorbed into it. First-appearance order is kept.
 */
export function rollUpThemeDisplayNames(
  selectedDisplayNames: string[],
  themeOptionValues: FilterOptionValue[],
): string[] {
  const selected = new Set(selectedDisplayNames.map(collapseThemeDisplayName))
  const parentOfChildName = new Map<string, string>()
  for (const [parent, children] of Object.entries(
    groupThemeValuesByParent(themeOptionValues),
  )) {
    if (children.length < 2) continue
    const childNames = children.map((c) => collapseThemeDisplayName(c.value))
    if (childNames.every((name) => selected.has(name))) {
      for (const name of childNames) parentOfChildName.set(name, parent)
    }
  }
  const rolledParents = new Set(parentOfChildName.values())

  const rolledUp = new Set<string>()
  for (const name of selectedDisplayNames) {
    const collapsed = collapseThemeDisplayName(name)
    const labelOnlyParent = name.split(PARENT_CHILD_SEPARATOR, 2)[0] ?? ''
    const parent =
      parentOfChildName.get(collapsed) ??
      (name.includes(PARENT_CHILD_SEPARATOR) &&
      rolledParents.has(labelOnlyParent)
        ? labelOnlyParent
        : undefined)
    rolledUp.add(parent ?? collapsed)
  }
  return [...rolledUp]
}
